import { extractNumberFromUnitString, isNeString, isNumber, isString } from '@aretheyhappy/utils';
/**
 * Helper class to do filtering on row data with filer lists provided by
 * AG Grid. This is used when using server provided data for AG Grid as
 * it will no longer filter internally.
 */
class AgGridFilterModelHelper {
    /**
     * Filter an array of row data objects using an AG Grid filter model.
     * Iterates over each key in `filterList` and applies the corresponding
     * filter (text or number) to `rows`, narrowing the result set in place.
     *
     * @param rows - The full array of row data to filter.
     * @param filterList - The AG Grid filter model, typically sourced from
     *   `request.filterModel` in a Server-Side Row Model (SSRM) callback.
     *   Each key maps to a `TextFilterModel`, `NumberFilterModel`, or a
     *   combined (`ICombinedSimpleModel`) variant supporting AND/OR logic.
     * @returns The filtered subset of `rows` that satisfies every active filter.
     */
    static filter(rows, filterList) {
        for (const filter in filterList) {
            rows = this._filter(rows, filter, filterList[filter]);
        }
        return rows;
    }
    /**
     * Dispatch a single filter entry to the appropriate handler based on
     * whether it is a combined (AND/OR) filter or a single-condition filter.
     *
     * @param rows - The current working set of row data.
     * @param key - The row data property key this filter targets.
     * @param filters - A single filter model or a combined filter model for `key`.
     * @returns The filtered subset of `rows`.
     */
    static _filter(rows, key, filters) {
        if ('operator' in filters)
            return this._multiFilter(rows, key, filters);
        else
            return this._singleFilter(rows, key, filters);
    }
    /**
     * Apply a combined (AND/OR) filter with two conditions to a row set.
     * Both `filter.conditions[0]` and `filter.conditions[1]` must be present;
     * rows are returned unchanged if fewer than two conditions are supplied.
     *
     * @param rows - The current working set of row data.
     * @param key - The row data property key this filter targets.
     * @param filter - A combined filter model containing an `operator` ("AND"/"OR")
     *   and at least two conditions.
     * @returns The filtered subset of `rows` that satisfies the combined condition.
     */
    static _multiFilter(rows, key, filter) {
        if (filter.conditions === undefined || filter.conditions.length < 2)
            return rows;
        const new_rows = [];
        for (const row of rows) {
            const matchesCondition1 = this._matchesFilter(row, key, filter.conditions[0]);
            const matchesCondition2 = this._matchesFilter(row, key, filter.conditions[1]);
            if (filter.operator === 'AND' && matchesCondition1 && matchesCondition2)
                new_rows.push(row);
            else if (filter.operator === 'OR' && (matchesCondition1 || matchesCondition2))
                new_rows.push(row);
        }
        return new_rows;
    }
    /**
     * Apply a single-condition filter to a row set.
     *
     * @param rows - The current working set of row data.
     * @param key - The row data property key this filter targets.
     * @param filter - A single `TextFilterModel` or `NumberFilterModel`.
     * @returns The filtered subset of `rows` that satisfies the condition.
     */
    static _singleFilter(rows, key, filter) {
        const new_rows = [];
        for (const row of rows) {
            if (this._matchesFilter(row, key, filter))
                new_rows.push(row);
        }
        return new_rows;
    }
    /**
     * Determine whether a single row satisfies a given filter condition by
     * delegating to the type-specific matcher (`_matchesNumberFilter` or
     * `_matchesTextFilter`). Logs a warning and returns `false` for any
     * unrecognised `filterType`.
     *
     * @param row - The individual row to evaluate.
     * @param key - The row data property key to test against.
     * @param filter - The filter condition to apply.
     * @returns `true` if the row matches the filter, `false` otherwise.
     */
    static _matchesFilter(row, key, filter) {
        if (filter.filterType === 'number')
            return this._matchesNumberFilter(row, key, filter);
        else if (filter.filterType === 'text')
            return this._matchesTextFilter(row, key, filter);
        else if (filter.filterType === 'set')
            return this._matchesSetFilter(row, key, filter);
        console.warn(`Unsupported filter type ${filter.filterType} sent to AgGridFilterModelHelper.filter`);
        return false;
    }
    /**
     * Check whether a row's numeric field satisfies a `NumberFilterModel` condition.
     * String values in the row are parsed via `extractNumberFromUnitString` to
     * support unit-bearing.
     *
     * Supported filter types: `blank`, `empty`, `notBlank`, `equals`, `notEqual`,
     * `lessThan`, `lessThanOrEqual`, `greaterThan`, `greaterThanOrEqual`, `inRange`.
     * Text-only types (`contains`, `notContains`, `startsWith`, `endsWith`) always
     * return `false` for number filters.
     *
     * @param row - The individual row to evaluate.
     * @param key - The row data property key whose value is tested.
     * @param filter - The AG Grid `NumberFilterModel` describing the condition.
     * @returns `true` if the row's value satisfies the filter, `false` otherwise.
     */
    static _matchesNumberFilter(row, key, filter) {
        let value = row[key];
        if (isString(value))
            value = extractNumberFromUnitString(value);
        else if (!isNumber(value))
            value = undefined;
        const comparison = isString(filter.filter) ? extractNumberFromUnitString(filter.filter) :
            (isNumber(filter.filter) ? filter.filter : undefined);
        const comparisonUpper = isString(filter.filterTo) ? extractNumberFromUnitString(filter.filterTo) :
            (isNumber(filter.filterTo) ? filter.filterTo : undefined);
        switch (filter.type) {
            case 'empty':
            case 'blank':
                return row[key] === undefined || row[key] === null || row[key] === '' || value === undefined;
            case 'notBlank':
                return isNumber(value);
            case 'equals':
                return value === comparison;
            case 'notEqual':
                return value !== comparison;
            case 'lessThan':
                if (value === undefined || comparison === undefined)
                    return false;
                return value < comparison;
            case 'lessThanOrEqual':
                if (value === undefined || comparison === undefined)
                    return false;
                return value <= comparison;
            case 'greaterThan':
                if (value === undefined || comparison === undefined)
                    return false;
                return value > comparison;
            case 'greaterThanOrEqual':
                if (value === undefined || comparison === undefined)
                    return false;
                return value >= comparison;
            case 'inRange':
                if (value === undefined || comparison === undefined || comparisonUpper === undefined)
                    return false;
                return value >= comparison && value <= comparisonUpper;
            case 'contains':
            case 'notContains':
            case 'startsWith':
            case 'endsWith':
                return false;
        }
        return false;
    }
    /**
     * Check whether a row's field satisfies a `TextFilterModel` condition.
     * Comparisons are case-insensitive; both the row value and filter value are
     * lower-cased before evaluation.
     *
     * Supported filter types: `blank`, `empty`, `notBlank`, `equals`, `notEqual`,
     * `contains`, `notContains`, `startsWith`, `endsWith`.
     * Numeric comparison types (`lessThan`, `lessThanOrEqual`, `greaterThan`,
     * `greaterThanOrEqual`, `inRange`) always return `false` for text filters.
     *
     * @param row - The individual row to evaluate.
     * @param key - The row data property key whose value is tested.
     * @param filter - The AG Grid `TextFilterModel` describing the condition.
     * @returns `true` if the row's value satisfies the filter, `false` otherwise.
     */
    static _matchesTextFilter(row, key, filter) {
        const value = `${row[key]}`.toLowerCase();
        const comparison = `${filter.filter}`.toLowerCase();
        switch (filter.type) {
            case 'empty':
            case 'blank':
                return row[key] === undefined || row[key] === null || row[key] === '';
            case 'notBlank':
                return isNeString(row[key]);
            case 'equals':
                return row[key] === filter.filter ||
                    value === comparison;
            case 'notEqual':
                return row[key] !== filter.filter &&
                    value !== comparison;
            case 'contains':
                return value.includes(comparison);
            case 'notContains':
                return !value.includes(comparison);
            case 'startsWith':
                return value.indexOf(comparison) === 0;
            case 'endsWith':
                return value.indexOf(comparison) ===
                    value.length - comparison.length;
            case 'lessThan':
            case 'lessThanOrEqual':
            case 'greaterThan':
            case 'greaterThanOrEqual':
            case 'inRange':
                return false; // Not supported for text
        }
        return false;
    }
    /**
     * Check whether a row's field satisfies a `SetFilterModel` condition.
     * Empty values (`undefined`, `null`, and empty strings) never match.
     * Non-empty row values are stringified before comparison so numeric row
     * values can be matched against AG Grid's string-based set filter values.
     *
     * @param row - The individual row to evaluate.
     * @param key - The row data property key whose value is tested.
     * @param filter - The AG Grid `SetFilterModel` describing the allowed values.
     * @returns `true` if the row's value is present in `filter.values`, `false` otherwise.
     */
    static _matchesSetFilter(row, key, filter) {
        if (row[key] === undefined || row[key] === null || row[key] === '')
            return false;
        return filter.values.includes(`${row[key]}`);
    }
}
export { AgGridFilterModelHelper };
export default AgGridFilterModelHelper;
//# sourceMappingURL=AgGridFilterModelHelper.js.map